// This code was copied from the file app/pages/upgrade/components/PlanCards/TierCard.tsx
// This was done in an attempt to minimize changes to the existing structure and add the new changes elsewhere
// until we're able to integrate this into the DreamBuilder project.

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RadioGroup } from '@headlessui/react';
import { CheckIcon, ClockIcon, DocumentTextIcon, TagIcon } from '@heroicons/react/24/outline';
import cx from 'classnames';

import Button from '~/components/Button';
import Typography from '~/components/Typography';
import type { Price, PriceInterval, Tier } from '~/interfaces/subscriptions';
import type { UpgradePage } from '~/interfaces/upgrade_page';
import normalizeFontType from '~/utils/normalizeFontType';

import Badge from '../../PlanCards/Badge';
import LineItem from '../../PlanCards/LineItem';
import GiftCardPriceBlock from './GiftCardPriceBlock';

interface Props {
  tier: Tier;
  upgradePage: UpgradePage;
  selectedPriceId: string;
  isExternalCheckoutEnabled: boolean;
  premiumOffer?: any;
  onPriceIdChange: (priceId: string) => void;
  onContinue: () => void;
}

const renderExpiresInXDays = (expiresAt: string) => {
  const expiresAtDate = new Date(expiresAt);
  const today = new Date();
  const timeDiff = expiresAtDate.getTime() - today.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

  if (daysDiff <= 0) {
    return 'Last day to redeem!';
  }

  if (daysDiff === 1) {
    return `Expires in ${daysDiff} day`;
  }

  return `Expires in ${daysDiff} days`;
};

const priceIntervalDisplays: Record<PriceInterval, string> = {
  month: 'upgrade.tabs.gift.interval.one_month',
  year: 'upgrade.tabs.gift.interval.one_year',
  one_time: 'upgrade.tabs.gift.interval.lifetime',
  donation: '',
};

const GiftCard = ({
  tier,
  upgradePage,
  selectedPriceId,
  isExternalCheckoutEnabled = false,
  onPriceIdChange,
  onContinue,
}: Props) => {
  const isActive = tier.prices.some((price) => price.id === selectedPriceId);
  const recommendedPrice = tier.prices.find((price) => price.id === upgradePage.main_section.most_popular_price_id);
  const recommendedText = upgradePage.main_section.most_popular_plan_label_text;

  const backgroundColor = upgradePage.main_section.card_bg_color;
  const accentColor = upgradePage.main_section.card_accent_color;
  const borderColor = upgradePage.main_section.card_border_color;
  const fontFamily = normalizeFontType(upgradePage.main_section.card_font_family);
  const cardPriceFontSize = upgradePage.main_section.card_price_font_size;
  const txtColor = upgradePage.main_section.card_text_color;
  const highlightBgColor = upgradePage.main_section.most_popular_plan_bg_color;
  const highlightTextColor = upgradePage.main_section.most_popular_plan_label_color;
  const tabBgColor = upgradePage.main_section.card_tab_bg_color;
  const tabPillBgColor = upgradePage.main_section.card_tab_pill_bg_color;
  const tabTxtColor = upgradePage.main_section.card_tab_text_color;
  const ctaBgColor = upgradePage.main_section.checkout_cta_bg_color;
  const ctaTextColor = upgradePage.main_section.checkout_cta_text_color;

  const priceTabsRef = useRef<HTMLDivElement>(null);

  const [selectedPrice, setSelectedPrice] = useState<Price>(recommendedPrice || tier.prices[0]);
  const isRecommendedPrice = selectedPrice?.id === upgradePage.main_section.most_popular_price_id;

  const handleChangeInTier = (_tierId: string) => {
    onPriceIdChange(selectedPrice.id);
  };

  const handleChangeInPrice = (priceId: string) => {
    const newSelectedPrice = tier.prices.find((price) => price.id === priceId);
    if (newSelectedPrice) {
      setSelectedPrice(newSelectedPrice);
      onPriceIdChange(priceId);
    }
  };

  const { t } = useTranslation();

  // only render the card if this tier has a gift price
  if (!selectedPrice) return null;

  return (
    <div
      key={tier.id}
      className={cx(
        'relative flex h-full w-72 rounded-lg border bg-white p-6 shadow-sm focus:outline-none lg:w-80 xl:w-96',
        {
          'cursor-pointer': !isExternalCheckoutEnabled,
        },
      )}
      style={{
        backgroundColor,
        border: isActive && !isExternalCheckoutEnabled ? `1px solid ${accentColor}` : `1px solid ${borderColor}`,
      }}
      onClick={(e) => {
        // @ts-expect-error
        if (priceTabsRef.current !== e.target || !priceTabsRef.current?.contains(e.target)) {
          handleChangeInTier(tier.id);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleChangeInTier(tier.id);
        }
      }}
    >
      <div className="flex flex-col justify-between w-full space-y-4">
        <div className="flex flex-col w-full space-y-4">
          <Typography size="xl" className="truncate" color={txtColor}>
            {tier.name}
          </Typography>

          {selectedPrice.premium_offer && (
            <Badge
              highlightBgColor={highlightBgColor}
              text={selectedPrice.premium_offer.name}
              fontFamily={fontFamily}
              highlightTextColor={highlightTextColor}
            />
          )}

          {isRecommendedPrice && !selectedPrice.premium_offer && (
            <Badge
              highlightBgColor={highlightBgColor}
              text={recommendedText}
              fontFamily={fontFamily}
              highlightTextColor={highlightTextColor}
            />
          )}

          <GiftCardPriceBlock
            fontFamily={fontFamily}
            priceFontSize={cardPriceFontSize}
            accentColor={accentColor}
            price={selectedPrice}
            txtColor={txtColor}
            isExternalCheckoutEnabled={isExternalCheckoutEnabled}
          />

          {/* Tier Prices tab (monthly/annual) */}
          {tier.prices.length > 1 && (
            <RadioGroup ref={priceTabsRef} value={selectedPrice.id} onChange={handleChangeInPrice}>
              <div className="flex flex-row w-full p-1 rounded-md gap-x-1" style={{ backgroundColor: tabBgColor }}>
                {tier.prices.map((price) => (
                  // biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
                  <RadioGroup.Option key={price.id} value={price.id} className={({ active }) => cx('w-1/2')}>
                    {({ checked }) => (
                      <div
                        className={cx(
                          'w-full cursor-pointer rounded-md px-2 text-center capitalize shadow-sm',
                          checked && 'border',
                        )}
                        style={{
                          backgroundColor: checked ? tabPillBgColor : undefined,
                          color: checked ? tabTxtColor : tabPillBgColor,
                        }}
                      >
                        <Typography size="xs">{t(priceIntervalDisplays[price.interval])}</Typography>
                      </div>
                    )}
                  </RadioGroup.Option>
                ))}
              </div>
            </RadioGroup>
          )}

          {/* Tier Description */}
          <Typography
            as="span"
            size="xs"
            weight="light"
            fontFamily={fontFamily}
            color={txtColor}
            className="break-words"
          >
            {tier.description}
          </Typography>

          {/* Tier Premium Offer */}
          {selectedPrice.premium_offer && (
            <div className="space-y-2">
              {selectedPrice.premium_offer.terms && (
                <LineItem
                  accentColor={accentColor}
                  fontFamily={fontFamily}
                  txtColor={txtColor}
                  text={selectedPrice.premium_offer.terms || ''}
                  Icon={TagIcon}
                />
              )}
              {selectedPrice.premium_offer.description && (
                <LineItem
                  accentColor={accentColor}
                  fontFamily={fontFamily}
                  txtColor={txtColor}
                  text={selectedPrice.premium_offer.description || ''}
                  Icon={DocumentTextIcon}
                />
              )}
              {selectedPrice.premium_offer.expires_at && (
                <LineItem
                  accentColor={accentColor}
                  fontFamily={fontFamily}
                  txtColor={txtColor}
                  text={renderExpiresInXDays(selectedPrice.premium_offer.expires_at || '')}
                  Icon={ClockIcon}
                />
              )}
            </div>
          )}

          {/* Tier Features */}
          {selectedPrice?.features?.length ? (
            <ul
              style={{
                borderTopColor: accentColor,
              }}
              className={cx({
                'space-y-3 pt-0 text-sm leading-6': true,
                'border-t pt-4': tier.description || selectedPrice.premium_offer,
              })}
            >
              {selectedPrice.features.map((feature: string) => (
                <li key={feature} className="flex gap-x-2">
                  <div style={{ color: accentColor }}>
                    <CheckIcon
                      className={cx({
                        'mt-0.5 h-4 w-4 flex-none': true,
                      })}
                      aria-hidden="true"
                    />
                  </div>
                  <Typography
                    size="xs"
                    weight="light"
                    fontFamily={fontFamily}
                    color={txtColor}
                    className="w-full break-words"
                  >
                    {feature}
                  </Typography>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {/* Tier Actions */}
        {selectedPrice && (
          <Button
            type="button"
            variant="primary"
            className={cx(fontFamily, 'flex w-full justify-center')}
            onClick={onContinue}
            style={{
              backgroundColor: ctaBgColor,
              color: ctaTextColor,
            }}
          >
            {t('upgrade.tabs.gift.cta')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default GiftCard;
